import React, { VFC, useCallback, useMemo } from 'react';

import { RadioButton } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ICombo, IComboSlotOption } from '@rbi-ctg/menu';
import { ListItemGroup } from 'components/list-item-group';
import { transformItemToProductListItemProps } from 'components/product-detail/utils';
import { ProductListItem } from 'components/product-list-item';
import { MenuObjectTypes } from 'enums/menu';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { useMenuContext } from 'state/menu';
import { useProductWizardContext } from 'state/product-wizard';

import { OptionsContainer } from './product-customization-options.styled';
import { IProductCustomizationOptionsProps } from './types';

type ComboSlotOptionWithPrice = IComboSlotOption & { price?: number };
type OptionsGroups = { header: string; options: ComboSlotOptionWithPrice[] }[];

export const ProductCustomizationOptions: VFC<IProductCustomizationOptionsProps> = ({
  comboSlot,
  comboSlotOptions,
  selectedItem,
  onSelectComboSlot,
}) => {
  const { priceForComboSlotSelection } = useMenuContext();
  const { selectedProduct } = useProductWizardContext();

  const { formatMessage } = useIntl();
  const { formatCalories } = useMenuCalories();

  const optionsGroups = useMemo<OptionsGroups>(() => {
    const regularGroup: ComboSlotOptionWithPrice[] = [];
    const premiumGroup: ComboSlotOptionWithPrice[] = [];
    comboSlotOptions.forEach(option => {
      if (option.isPremium) {
        const premiumUpsellPrice = comboSlot
          ? priceForComboSlotSelection({
              combo: selectedProduct as ICombo,
              comboSlot,
              selectedOption: option,
            })
          : 0;
        premiumGroup.push({
          ...option,
          price: premiumUpsellPrice,
        });
      } else {
        regularGroup.push(option);
      }
    });
    return [
      { header: '', options: regularGroup },
      { header: formatMessage({ id: 'premiumOptions' }), options: premiumGroup },
    ];
  }, [comboSlot, comboSlotOptions, formatMessage, priceForComboSlotSelection, selectedProduct]);

  const handleSelectComboSlot = useCallback(
    (comboSlotOption: IComboSlotOption) => {
      const item = comboSlotOption.option;
      if (item._type !== MenuObjectTypes.ITEM) {
        return;
      }
      onSelectComboSlot(comboSlotOption);
    },
    [onSelectComboSlot]
  );

  // This prevents the hiddenInput inside RadioButton to be focused on press
  const RadioButtonContainer = styled.span`
    label input[type='radio'] {
      position: relative;
    }
  `;

  return (
    <OptionsContainer data-testid="product-customization-options">
      {optionsGroups.map(group => (
        <ListItemGroup
          key={group.header}
          header={group.header}
          items={group.options.map(comboSlotOption => {
            return (
              <ProductListItem
                key={comboSlotOption.option._id}
                isClickable
                onProductClick={() => handleSelectComboSlot(comboSlotOption)}
                productProps={{
                  ...transformItemToProductListItemProps(comboSlotOption.option).productProps,
                  calories: formatCalories(comboSlotOption.option.nutrition?.calories),
                  price: comboSlotOption.price,
                }}
                controls={
                  <RadioButtonContainer>
                    <RadioButton
                      checked={comboSlotOption.option._id === selectedItem._id}
                      label=""
                    />
                  </RadioButtonContainer>
                }
              />
            );
          })}
        />
      ))}
    </OptionsContainer>
  );
};
